import { SvgIcon } from "@progress/kendo-react-common";
import { Popup } from "@progress/kendo-react-popup";
import { xIcon } from "@progress/kendo-svg-icons";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isArrayEmpty } from "@/app/utils/helpers";
import { ICON_BASE_PATH } from "../../../../utils/config";
import "./AppCustomDropDown.scss";

interface AppCustomDropDownProps {
  data: any;
  name: string;
  value: string;
  selected: string | number | null;
  onChange: (value: any) => void;
  placeholder?: string;
  hideClear?: boolean;
}

export default function AppCustomDropDown({
  data,
  name,
  value,
  selected,
  onChange,
  placeholder,
  hideClear = false,
}: Readonly<AppCustomDropDownProps>) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [showClearIcon, setShowClearIcon] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const listboxId = useId();

  const handleSelect = (item: any) => {
    if (selected === item[value]) {
      onChange(null);
    } else {
      onChange(item[value]);
    }

    setOpen(false);
    triggerRef.current?.focus();
  };

  const selectedItem = selected
    ? data.find((item: any) => item[value] === selected)
    : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        anchorRef.current &&
        !anchorRef.current.contains(target) &&
        popupRef.current &&
        !popupRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!selected) {
      setShowClearIcon(false);
    }
  }, [selected]);

  const handleTriggerKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ":
        e.preventDefault();
        setOpen(true);
        break;

      case "Escape":
        setOpen(false);
        break;
    }
  };

  return (
    <div ref={anchorRef}>
      <button
        ref={triggerRef}
        type="button"
        className="gx-approval-dropdown"
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
      >
        <span
          className={`gx-font-semibold text-[0.875rem] !mb-0 ${
            selected ? "text-black" : "text-[#7a7a7a]"
          }`}
        >
          {selected
            ? selectedItem?.[name]
            : (placeholder ?? "")}
        </span>

        {!showClearIcon ? (
          <img
            src={`${ICON_BASE_PATH}down_arrow.svg`}
            width={17}
            className={`ms-1 transition-transform duration-300 ${
              open ? "-rotate-180" : ""
            }`}
            alt=""
            aria-hidden="true"
            onMouseEnter={() => {
              if (selected && !hideClear) {
                setShowClearIcon(true);
              }
            }}
          />
        ) : (
          <span
            role="button"
            tabIndex={0}
            aria-label={t("Clear selection")}
            className="gx-accf-clear-btn"
            onMouseLeave={() => setShowClearIcon(false)}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setShowClearIcon(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onChange(null);
                setShowClearIcon(false);
              }
            }}
          >
            <SvgIcon icon={xIcon} />
          </span>
        )}
      </button>

      <Popup anchor={anchorRef.current} show={open}>
        <div
          ref={popupRef}
          id={listboxId}
          role="listbox"
          className={`gx-approval-dropdown-tile ${
            isArrayEmpty(data)
              ? "flex items-center justify-center"
              : ""
          }`}
        >
          {isArrayEmpty(data) ? (
            <div>{t("no_data")}</div>
          ) : (
            <ul>
              {data.map((item: any) => (
                <li key={item[value]}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected === item[value]}
                    className={`gx-approval-dropdown-tile-list w-full flex ${
                      selected === item[value]
                        ? "bg-[gainsboro]"
                        : ""
                    }`}
                    onClick={() => handleSelect(item)}
                  >
                    <span className="gx-font-medium text-[0.875rem]">
                      {item[name]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Popup>
    </div>
  );
}